import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AppContext';
import { toast } from 'sonner';
import { X, Sparkles, Loader2 } from 'lucide-react';

/**
 * Mini-coach prompt shown ONCE when the user has rejected the last 3
 * Coach YUMIX progression recommendations in a row. Lets them switch their
 * progression aggressiveness so the algo bumps less aggressively.
 *
 * The prompt is dismissable per-month via localStorage so it doesn't
 * pester users who actively chose to ignore it.
 */
const ProgressionAggressivenessPrompt = ({ language = 'fr', onClose }) => {
  const fr = language === 'fr';
  const t = (frTxt, enTxt) => (fr ? frTxt : enTxt);

  const [shouldShow, setShouldShow] = useState(false);
  const [current, setCurrent] = useState('standard');
  const [consecutive, setConsecutive] = useState(0);
  const [saving, setSaving] = useState(null); // value being saved
  const dismissKey = (() => {
    const d = new Date();
    return `yumix_aggr_prompt_dismissed_${d.getFullYear()}_${d.getMonth() + 1}`;
  })();

  useEffect(() => {
    let cancelled = false;
    // Honor monthly dismiss
    try {
      if (localStorage.getItem(dismissKey) === '1') return;
    } catch { /* */ }

    api.get('/workouts/aggressiveness-check')
      .then(res => {
        if (cancelled) return;
        const d = res.data || {};
        if (d.should_prompt) {
          setConsecutive(d.consecutive_rejections || 0);
          setCurrent(d.current_aggressiveness || 'standard');
          setShouldShow(true);
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickOption = async (value) => {
    setSaving(value);
    try {
      await api.post('/workouts/set-aggressiveness', { aggressiveness: value });
      try { localStorage.setItem(dismissKey, '1'); } catch { /* */ }
      toast.success(t(
        `Progression ajustée → ${labels(value).fr}`,
        `Progression adjusted → ${labels(value).en}`,
      ));
      setShouldShow(false);
      if (onClose) onClose();
    } catch {
      toast.error(t('Erreur de sauvegarde', 'Save error'));
    } finally {
      setSaving(null);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, '1'); } catch { /* */ }
    setShouldShow(false);
    if (onClose) onClose();
  };

  if (!shouldShow) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      data-testid="aggressiveness-prompt"
      onClick={dismiss}
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card border border-amber-500/40 rounded-2xl max-w-md w-full p-5 shadow-2xl"
        style={{ boxShadow: '0 0 40px rgba(251,191,36,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-amber-300" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
              {t('Coach YUMIX · Ajustement', 'Coach YUMIX · Adjustment')}
            </p>
            <h2 className="font-heading text-base uppercase tracking-tight text-foreground leading-tight">
              {t('Cible trop ambitieuse ?', 'Target too ambitious?')}
            </h2>
          </div>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground p-1 shrink-0"
            data-testid="dismiss-aggressiveness-prompt"
            aria-label={t('Fermer', 'Dismiss')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <p className="text-xs text-muted-foreground leading-relaxed mb-4">
          {fr
            ? `Tu as rejete ${consecutive} suggestions consecutives. On peut ajuster l'agressivite de Coach YUMIX pour mieux coller a ta realite. Choisis ton mode :`
            : `You've rejected ${consecutive} consecutive suggestions. We can adjust the Coach YUMIX's aggressiveness to better match your reality. Pick your mode:`}
        </p>

        {/* Options */}
        <div className="space-y-2">
          {['conservative', 'standard', 'aggressive'].map((opt) => {
            const isCurrent = current === opt;
            const isSaving = saving === opt;
            const meta = labels(opt);
            return (
              <button
                key={opt}
                onClick={() => pickOption(opt)}
                disabled={!!saving}
                className={`w-full text-left p-3 rounded-xl border transition-all active:scale-[0.99] ${
                  isCurrent
                    ? 'border-amber-500/50 bg-amber-500/10'
                    : 'border-border/50 bg-secondary/30 hover:border-amber-500/40'
                } ${saving ? 'opacity-60' : ''}`}
                data-testid={`aggressiveness-option-${opt}`}
              >
                <div className="flex items-start gap-2.5">
                  <span className="text-lg shrink-0 mt-0.5" aria-hidden="true">{meta.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-heading uppercase tracking-tight text-foreground flex items-center gap-2">
                      {fr ? meta.fr : meta.en}
                      {isCurrent && (
                        <span className="text-[9px] uppercase tracking-wider text-amber-400 font-bold">
                          · {t('Actuel', 'Current')}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                      {fr ? meta.desc_fr : meta.desc_en}
                    </p>
                  </div>
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin text-amber-400 shrink-0 mt-0.5" />}
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-[10px] text-muted-foreground/60 italic text-center mt-3">
          {t(
            'Tu peux changer ce reglage plus tard dans tes preferences.',
            'You can change this later in your preferences.',
          )}
        </p>
      </motion.div>
    </div>
  );
};

const labels = (value) => {
  const map = {
    conservative: {
      emoji: '🐢',
      fr: 'Conservateur',
      en: 'Conservative',
      desc_fr: 'Bumps plus petits (+2.5 lbs max). Idéal si tu reviens de blessure ou si tu rates souvent tes cibles.',
      desc_en: 'Smaller bumps (+2.5 lbs max). Best after injury or if you often miss your targets.',
    },
    standard: {
      emoji: '🎯',
      fr: 'Standard',
      en: 'Standard',
      desc_fr: '+5 lbs sur les compounds, +2.5 lbs en isolation. Le default équilibré.',
      desc_en: '+5 lbs on compounds, +2.5 lbs on isolation. The balanced default.',
    },
    aggressive: {
      emoji: '🔥',
      fr: 'Agressif',
      en: 'Aggressive',
      desc_fr: '+7.5 lbs sur les compounds, +5 lbs en isolation. Pour les progressions rapides.',
      desc_en: '+7.5 lbs on compounds, +5 lbs on isolation. For fast progression cycles.',
    },
  };
  return map[value] || map.standard;
};

export default ProgressionAggressivenessPrompt;
